/**
 * Sign-in for a device that already holds a passkey, and the entry point to the
 * cross-device flows. There is deliberately no "forgot your…": with passkeys
 * and no email there is nothing to forget, and nothing to recover either.
 */
import { useState } from 'react'
import { signIn } from './passkey'
import type { Account } from './session'

export function SignIn({
  onDone,
  onCreate,
  notice,
}: {
  onDone: (account: Account) => void
  onCreate: () => void
  /** Why they are here when they did not ask to be — the server's own sentence
   *  for a session it has stopped honouring. */
  notice?: string | null
}): JSX.Element {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const go = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await signIn()
      onDone(result.user)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No passkey was offered')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page">
      <div className="wordmark">
        cards{' '}
        <span className="suit">
          <span className="s-s">♠</span>
          <span className="s-h">♥</span>
          <span className="s-d">♦</span>
          <span className="s-c">♣</span>
        </span>
      </div>
      <section className="live" style={{ marginTop: 'auto', marginBottom: 'auto' }}>
        <h2>{notice ? 'Sign in again' : 'Welcome back'}</h2>
        <p>
          {notice
            ? `${notice} Your passkey is still on this device — it is one tap.`
            : 'Your passkey is on this device, or on a phone you can scan with. Either way it is one tap.'}
        </p>
        {error ? <p className="error">{error}</p> : null}
        <button className="btn btn-primary btn-block" disabled={busy} onClick={() => void go()}>
          {busy ? 'Waiting for your device…' : 'Sign in with a passkey'}
        </button>
        <button className="btn btn-quiet btn-block" onClick={onCreate}>
          Create an account instead
        </button>
      </section>
    </div>
  )
}
